import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Table, Checkbox, Button, Icon, Loader, Modal, Header } from 'semantic-ui-react';
// Import CSS module
import styles from '../../styles/VariationVersionsList.module.css';
// Import upgraded action types
import { ACTION_TYPES_UPGRADE } from '../../../constants/config_upgrade';
// Import URL signing utility
import { fetchUserSignedUrlPathV2 } from '../../../utils/fetchSignedUrlByFolderV2';
import { useSelector } from 'react-redux';
// Import the edit version manager hook for direct delete functionality
import useEditVersionManager from '../../hooks/useEditVersionManager_upgrade';

/**
 * UPGRADED VariationVersionsList component with enhanced delete functionality
 * Includes delete trash can with confirmation modal
 * Works with the new Redux-based action system
 * OPTIMIZED: Memoized to prevent unnecessary re-renders during text editing
 */
const VariationVersionsListUpgrade = ({
  variationId,
  characterId,
  samples,
  activeSampleUuid,
  previewedSampleUuid,
  onAction, // Single consolidated callback
  isLoading
}) => {
  const [playingSampleId, setPlayingSampleId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [signedUrls, setSignedUrls] = useState({});
  const [localPreviewedSampleUuid, setLocalPreviewedSampleUuid] = useState(null);
  const audioRef = useRef(new Audio());

  // Get userId from Redux
  const userId = useSelector(state => state.user.profile?.data?.userId);

  // Use the edit version manager hook for direct delete functionality
  const { deleteVersionById } = useEditVersionManager();

  // 🔍 DETAILED PROPS LOGGING - Track what props are received
  console.log('[VERSIONS_LIST_RECEIVED_PROPS] VariationVersionsList received props:', {
    variationId,
    activeSampleUuid,
    previewedSampleUuid,
    samples: samples?.map(s => ({
      uuid: s.uuid,
      isActive: s.isActive,
      version: s.version,
      textSnippet: s.textSnippet?.substring(0, 30) + '...'
    })),
    isLoading
  });

  // Conditional debug logging for performance
  if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_VERSIONS) {
    console.log('[VariationVersionsListUpgrade] activeSampleUuid prop:', activeSampleUuid);
    console.log('[VariationVersionsListUpgrade] samples:', samples?.map(s => ({ uuid: s.uuid, isActive: s.isActive })));
  }

  // Sign URLs for audio playback
  useEffect(() => {
    const signUrls = async () => {
      if (!samples || !userId) return;

      const newSignedUrls = {};
      for (const sample of samples) {
        if (sample.s3Url && !signedUrls[sample.uuid]) {
          try {
            const signedUrl = await fetchUserSignedUrlPathV2(userId, sample.s3Url);
            newSignedUrls[sample.uuid] = signedUrl;
          } catch (error) {
            console.error('Error signing URL for sample:', sample.uuid, error);
          }
        }
      }

      if (Object.keys(newSignedUrls).length > 0) {
        setSignedUrls(prev => ({ ...prev, ...newSignedUrls }));
      }
    };

    signUrls();
  }, [samples, userId, signedUrls]);

  // Consolidated action handler
  const actionHandler = useCallback((e, actionType, versionSample, additionalData = {}) => {
    // Only stop propagation for button clicks, not row clicks
    if (actionType !== 'SAMPLE_EDIT_SELECT') {
      e.stopPropagation();
    }

    // Enhanced logging for debugging row selection
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ACTION_HANDLER] ${actionType.toUpperCase()} action for versionSample:`, versionSample.uuid, 'SAMPLE:', versionSample);
    }

    // Set immediate visual feedback for row highlighting
    if (actionType === 'SAMPLE_EDIT_SELECT') {
      setLocalPreviewedSampleUuid(versionSample.uuid);
    }

    const actionPayload = {
      actionType: actionType,
      versionSample: versionSample,
      variationId: variationId,
      characterId: characterId,
      activeSampleUuid: activeSampleUuid,
      ...additionalData
    };

    // Handle delete action with confirmation modal
    if (actionType === 'DELETE_VERSION') {
      setVersionToDelete(versionSample);
      setShowDeleteModal(true);
      return;
    }

    // Send consolidated action to parent
    if (onAction) {
      onAction(actionPayload);
    }
  }, [variationId, activeSampleUuid, characterId, onAction]);

  // Handle confirmed delete - using direct delete functionality
  const handleConfirmDelete = useCallback(async () => {
    if (!versionToDelete) return;

    setIsDeleting(true);

    try {
      console.log('[DELETE_VERSION] Deleting speech sample:', versionToDelete.uuid, 'from variation:', variationId);

      // Use the correct delete function for individual speech samples
      await deleteVersionById(versionToDelete.uuid, variationId);

      console.log('[DELETE_VERSION] Speech sample deleted successfully');

      // Notify parent of successful deletion for UI updates
      if (onAction) {
        await onAction({
          actionType: 'DELETE_VERSION',
          versionSample: versionToDelete,
          variationId: variationId,
          characterId: characterId
        });
      }

      // Close modal and reset state
      setShowDeleteModal(false);
      setVersionToDelete(null);
    } catch (error) {
      console.error('[DELETE_VERSION] Error deleting speech sample:', error);
      // TODO: Show error message to user
    } finally {
      setIsDeleting(false);
    }
  }, [versionToDelete, variationId, characterId, deleteVersionById, onAction]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setVersionToDelete(null);
  }, []);

  // Row visual styling based on state - CSS class approach
  const getRowVisualAttributes = useCallback((sampleUuid, previewedSampleUuid, activeSampleUuid, versionSample) => {
    const isActive = sampleUuid === activeSampleUuid;
    const isPreviewed = sampleUuid === previewedSampleUuid && !isActive;
    // console.log('[ROW_HIGHLIGHT] activeSampleUuid:', activeSampleUuid, typeof activeSampleUuid);
    // console.log('[ROW_HIGHLIGHT] sampleUuid:', sampleUuid, typeof sampleUuid);
    // console.log('[ROW_HIGHLIGHT] isActive:', isActive);

    return {
      className: `${styles.row} ${isActive ? styles.active : ''} ${isPreviewed ? styles.previewed : ''}`.trim(),
      'data-versionsample-id': sampleUuid,
      'data-state': isActive ? 'active' : isPreviewed ? 'previewed' : 'normal'
    };
  }, []);

  const handlePlayClick = (versionSample) => {
    // If this versionSample is already playing, pause it
    if (playingSampleId === versionSample.uuid && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    // If another versionSample is playing, stop it
    if (playingSampleId && playingSampleId !== versionSample.uuid) {
      audioRef.current.pause();
    }

    // Play the new versionSample
    // Use signed URL if available, fallback to audioUrl
    const audioUrl = signedUrls[versionSample.uuid] || versionSample.audioUrl;
    if (audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setPlayingSampleId(versionSample.uuid);
        })
        .catch(error => {
          console.error('Error playing audio:', error);
        });
    }
  };

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;

    const handleEnded = () => {
      setIsPlaying(false);
      setPlayingSampleId(null);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Loader active inline='centered' />
        <p>Loading variation versions...</p>
      </div>
    );
  }

  if (!samples || samples.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        No versions available for this variation.
      </div>
    );
  }

  return (
    <div className="variation-versions-list">
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>Active</Table.HeaderCell>
            <Table.HeaderCell width={2}>Version</Table.HeaderCell>
            <Table.HeaderCell width={6}>Text Content</Table.HeaderCell>
            <Table.HeaderCell width={2}>Duration</Table.HeaderCell>
            <Table.HeaderCell textAlign='center' width={3}>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {samples.map((versionSample) => {
            // Use local state for immediate feedback, fallback to prop
            const effectivePreviewedUuid = localPreviewedSampleUuid || previewedSampleUuid;
            const rowAttributes = getRowVisualAttributes(versionSample.uuid, effectivePreviewedUuid, activeSampleUuid, versionSample);

            return (
              <Table.Row
                key={versionSample.uuid}
                {...rowAttributes}
                onClick={(e) => actionHandler(e, 'SAMPLE_EDIT_SELECT', versionSample)}
              >
                <Table.Cell textAlign='center' onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    // 🔍 CHECKBOX STATE LOGGING - Track checkbox logic
                    const shouldBeChecked = versionSample.uuid === activeSampleUuid;
                    console.log('[CHECKBOX_STATE] Checkbox render for sample:', {
                      sampleUuid: versionSample.uuid,
                      activeSampleUuid,
                      isActive: versionSample.isActive,
                      shouldBeChecked,
                      uuidMatch: versionSample.uuid === activeSampleUuid,
                      sampleUuidType: typeof versionSample.uuid,
                      activeSampleUuidType: typeof activeSampleUuid
                    });

                    return (
                      <Checkbox
                        checked={shouldBeChecked}
                        onChange={(e) => actionHandler(e, 'SET_ACTIVE_SAMPLE', versionSample)}
                        onClick={(e) => e.stopPropagation()}
                        title="Set as active sample"
                      />
                    );
                  })()}
                </Table.Cell>

                <Table.Cell>
                  <strong>V{versionSample.version || '1'}</strong>
                  {versionSample.uuid === activeSampleUuid && (
                    <Icon name="star" color="yellow" style={{ marginLeft: '5px' }} title="Active version" />
                  )}
                </Table.Cell>

                <Table.Cell>
                  <div style={{
                    maxHeight: '60px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.9em'
                  }}>
                    {versionSample.textSnippet || 'No text available'}
                  </div>
                </Table.Cell>

                <Table.Cell textAlign='right'>
                  {versionSample.fileDuration ? `${versionSample.fileDuration}s` : 'N/A'}
                </Table.Cell>

                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Button.Group size="mini">
                    <Button
                      type="button"
                      icon
                      size='tiny'
                      color={isPlaying && playingSampleId === versionSample.uuid ? 'red' : 'orange'}
                      disabled={!(signedUrls[versionSample.uuid] || versionSample.audioUrl || versionSample.s3Url)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayClick(versionSample);
                      }}
                      title={isPlaying && playingSampleId === versionSample.uuid ? 'Pause audio' : 'Play audio'}
                    >
                      <Icon name={isPlaying && playingSampleId === versionSample.uuid ? 'pause' : 'play'} />
                    </Button>

                    <Button
                      type="button"
                      icon="edit"
                      title="Edit this version"
                      color="green"
                      onClick={(e) => actionHandler(e, 'SAMPLE_EDIT_SELECT', versionSample)}
                    />

                    <Button
                      icon="copy"
                      title="Use as Base for new variation"
                      color="blue"
                      onClick={(e) => actionHandler(e, 'SAMPLE_USE_AS_BASE', versionSample)}
                    />

                    <Button
                      icon="trash"
                      color="red"
                      title="Delete this version"
                      onClick={(e) => actionHandler(e, 'DELETE_VERSION', versionSample)}
                      disabled={samples.length <= 1} // Prevent deleting the last version
                    />
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        size="small"
      >
        <Header icon>
          <Icon name="trash alternate" />
          Delete Version
        </Header>
        <Modal.Content>
          <div style={{ textAlign: 'center' }}>
            <p>
              Are you sure you want to delete <strong>Version {versionToDelete?.version}</strong> of this variation?
            </p>
            <p style={{ color: '#2185d0', fontWeight: 'bold' }}>
              <Icon name="info circle" />
              This will delete ONLY this version, not the entire variation.
            </p>
            {versionToDelete?.textSnippet && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '4px',
                fontStyle: 'italic',
                color: '#666'
              }}>
                "{versionToDelete.textSnippet.substring(0, 100)}{versionToDelete.textSnippet.length > 100 ? '...' : ''}"
              </div>
            )}
            <p style={{ marginTop: '15px', color: '#d01919' }}>
              <Icon name="warning sign" />
              This action cannot be undone. The speech sample and its associated voice recording will be permanently deleted.
            </p>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            <Icon name="remove" />
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            loading={isDeleting}
            disabled={isDeleting}
          >
            <Icon name="checkmark" />
            Yes, Delete Version
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  // Compare primitive values
  if (prevProps.variationId !== nextProps.variationId) return false;
  if (prevProps.characterId !== nextProps.characterId) return false;
  if (prevProps.activeSampleUuid !== nextProps.activeSampleUuid) return false;
  if (prevProps.previewedSampleUuid !== nextProps.previewedSampleUuid) return false;
  if (prevProps.isLoading !== nextProps.isLoading) return false;

  // Deep compare samples array
  if (prevProps.samples?.length !== nextProps.samples?.length) return false;

  // Compare each sample (shallow comparison of key properties)
  for (let i = 0; i < (prevProps.samples?.length || 0); i++) {
    const prevSample = prevProps.samples[i];
    const nextSample = nextProps.samples[i];

    if (prevSample?.uuid !== nextSample?.uuid) return false;
    if (prevSample?.textSnippet !== nextSample?.textSnippet) return false;
    if (prevSample?.version !== nextSample?.version) return false;
    if (prevSample?.isActive !== nextSample?.isActive) return false;
  }

  // onAction function comparison (skip - assume stable if memoized properly)

  return true; // Props are equal, skip re-render
};

// Export action types for parent components
export { ACTION_TYPES_UPGRADE };

// Export memoized component
export default React.memo(VariationVersionsListUpgrade, arePropsEqual);
